import React, { useCallback, useState } from 'react';
import 'react-contexify/dist/ReactContexify.css';
import { Menu, Item, ItemParams, PredicateParams, Separator } from 'react-contexify';
import { useDispatch, useSelector } from 'react-redux';
import { WidgetState, WidgetType } from '@kemu-io/kemu-core/types';
import { BrowserJsPlumbInstance } from '@jsplumb/community';
import { message } from 'antd';
import Icon, {
	ApiOutlined,
	AppstoreAddOutlined,
	CodeOutlined,
	CopyOutlined,
	DeleteOutlined,
	DisconnectOutlined,
	EditOutlined,
	FolderOutlined,
	ForkOutlined,
	MinusCircleOutlined,
	PauseCircleOutlined,
	PlayCircleOutlined,
	PlusCircleOutlined,
	ReloadOutlined,
	SaveOutlined,
	ScissorOutlined,
	SnippetsOutlined,
} from '@ant-design/icons';
import { ReactComponent as SnippetsMenuIcon } from '@src/assets/img/menus/snippets.svg';
import {
	duplicateWidgetAction,
	selectSelectedWidgets,
	showCreateWidgetModalAction,
	showCreateWidgetBundleModalAction,
	showCreateIntegrationModalAction,
	toggleWidgetState,
	groupSelectedWidgetsAction,
	unlinkCustomWidgetAction,
	selectLastMouseClick
} from '../logicMapperSlice.ts';
import {
	selectedBlock,
	selectVisibleGroup,
	selectExecutionMode,
	selectEditingSnapshot,
	selectEditingModeWidgetIds,
	preserveWidgetChangesAction,
} from '../../Workspace/workspaceSlice';
import { findRecipe } from '@kemu-io/kemu-core/common/recipeCache';
import { safeJSONStringify } from '@common/utils';
import { AppDispatch } from '@src/app/store';
import { clipboardHasValidKemuStructure } from '../reducers/clipboardUtils';
import { pasteFromClipboardAction } from '../reducers/clipboardReducers';
import { createSnippetFromSelectionAction } from '../reducers/createSnippetReducer';
import useWidgetClipboardActions from '../hooks/useWidgetClipboardActions';
import styles from './contextMenu.module.css';
import useTranslation from '@hooks/useTranslation';
import useCoreBreakpoints from '@hooks/useCoreBreakpoints';
import { decodeDomId, DOMElementInfo } from '@common/utils';
import { selectLogicMapperSettings, showWidgetLauncher } from '@src/features/interface/interfaceSlice.ts';
import { useWidgetState } from '@hooks/index.ts';
import { resetWidgetGroupFields } from '@src/app/recipe/utils';

/** Id of the context menu for gates */
export const MENU_GATE = 'gate-menu';
/** Id of the context menu for blocks */
export const MENU_BLOCK = 'block-menu';
/** Id of the context menu for the canvas */
// export const MENU_CANVAS = 'canvas-menu';
/** Id of the context menu for the logic maker */
export const MENU_LOGIC_MAKER = 'logic-maker-menu';

/** Finds the first parent node with the given className */
export const findMenuBoundary = (el: HTMLElement | undefined, className: string | undefined): HTMLElement | null => {
	let parentEl: HTMLElement | null | undefined = el;
	while (parentEl) {
		if (className && parentEl.classList.contains(className)) { return parentEl; }
		parentEl = parentEl.parentElement;
	}

	return null;
};

export interface ContextMenuItem {
	title: string;
}

export interface EventProps {
	target: HTMLElement;
	/** a class name in the parent elements to search for */
	boundary: string;
}

export interface WidgetMenuProps {
	boundaryClass: string;
	disabled: boolean;
	recipeId: string;
	gateId: string;
	blockId: string;
	gateType: WidgetType;
	returnOriginalEvent: boolean;
}

interface Props {
	plumbInstance: BrowserJsPlumbInstance | null;
}

type ContextMenuItemContentProps = {
	/** Icon rendered to the left of the label. */
	icon: React.ReactNode;
	/** Label content shown to the user. */
	label: React.ReactNode;
};

/** Renders a standardized layout for a context menu item with an icon and label. */
const ContextMenuItemContent = ({ icon, label }: ContextMenuItemContentProps): React.JSX.Element => {
	return (
		<span className={styles.ItemContent}>
			<span className={styles.ItemIcon}>{icon}</span>
			<span>{label}</span>
		</span>
	);
};


const hideIfDisabled = ({ props, data }: PredicateParams<WidgetMenuProps>) => {
	if (props?.gateType === WidgetType.note) {
		return true;
	}

	if (data.key === 'disable-widget') {
		return !!props?.disabled;
	} else {
		return !props?.disabled;
	}
};

const hideIfNotGroupWidget = ({ props }: PredicateParams<WidgetMenuProps>) => {
	return props?.gateType !== WidgetType.widgetGroup;
};

const hideIfWidgetGroup = ({ props }: PredicateParams<WidgetMenuProps>) => {
	return props?.gateType === WidgetType.widgetGroup;
};


const ContextMenu = (props: Props): React.JSX.Element => {
	const dispatch = useDispatch<AppDispatch>();
	const selectedWidgets = useSelector(selectSelectedWidgets);
	const lastMouseClick = useSelector(selectLastMouseClick);
	const currentBlock = useSelector(selectedBlock);
	const [pasteEnabled, setPasteEnabled] = useState(false);
	const currentGroup = useSelector(selectVisibleGroup);
	const userSettings = useSelector(selectLogicMapperSettings);
	const executionMode = useSelector(selectExecutionMode);
	const editingSnapshot = useSelector(selectEditingSnapshot);
	const editingModeWidgetIds = useSelector(selectEditingModeWidgetIds);
	const { hasBreakpoint, setBreakpoint, removeBreakpoint } = useCoreBreakpoints();
	const { copySelection, cutSelection, removeSelection } = useWidgetClipboardActions();
	const { getState } = useWidgetState<WidgetState>(
		currentBlock?.recipePoolId || '',
		currentBlock?.recipeId || '',
		currentGroup?.groupId || ''
	);

	// const userSettings = useSelector(selectUserSettings);
	// const canCreateWidgetBundles = userSettings?.useWidgetBundles;

	const t = useTranslation('ContextMenu');

	const showWhenMultipleWidgetsAreSelected = useCallback(() => {
		return selectedWidgets.length < 2;
	}, [selectedWidgets]);

	const handleGateDuplication = (el: HTMLElement) => {
		const info = decodeDomId(el.id);
		if (info && info.gateId) {
			dispatch(duplicateWidgetAction({
				recipeId: info.recipeId,
				thingRecipeId: info.blockId,
				widgetThingId: info.gateId
			}));
		}
	};

	// const handleMakeOriginalEvent = (el: HTMLElement) => {
	// 	const info = decodeDomId(el.id);
	// 	if (info && info.gateId) {
	// 		dispatch(toggleWidgetOutputMode({
	// 			recipeId: info.recipeId,
	// 			blockRecipeId: info.blockId,
	// 			sourceGateId: info.gateId
	// 		}));
	// 	}
	// };

	const handleRemoveWidget = async (el: HTMLElement) => {
		const info = decodeDomId(el.id);
		const removeMultiple = Boolean(selectedWidgets.length);
		if (info && info.gateId) {
			const idsToRemove = removeMultiple ? selectedWidgets.map(w => w.widgetId) : [info.gateId];
			removeSelection({
				recipeId: info.recipeId,
				thingId: info.blockId,
				widgetIds: idsToRemove,
			});
			// dispatch(removeWidgetsAction({
			// 	recipeId: info.recipeId,
			// 	thingId: info.blockId,
			// 	widgetsIds: idsToRemove,
			// 	clearSelection: removeMultiple,
			// }));

			// if (removeMultiple) {
			// 	props.plumbInstance?.clearDragSelection();
			// }
		}
	};

	/* const handleBlockDuplication = (el: HTMLElement) => {
		const info = decodeDomId(el.id);
		if(info && info.blockId){
			dispatch(duplicateBlock(info.blockId));
		}
	}; */


	const menuItemSelected = useCallback(({ props, data, triggerEvent }: ItemParams) => {
		const eventHandler = data.handler;
		if (!props || ! props.boundaryClass) { throw new Error('boundaryClass not defined. You probably forgot to pass the boundary class via props: show(event, {props: { boundaryClass: "myParentClass" } })'); }
		const parentEl = findMenuBoundary(triggerEvent.target as HTMLElement, props.boundaryClass);
		if (parentEl) {
			if (typeof eventHandler === 'function') {
				eventHandler(parentEl);
			} else {
				console.log('Unmapped event', eventHandler, parentEl);
			}
		}
	}, []);

	const toggleWidget = useCallback((disabled: boolean, info: DOMElementInfo | null) => {
		if (info && info.gateId) {
			dispatch(toggleWidgetState({
				recipeId: info.recipeId,
				blockRecipeId: info.blockId,
				sourceGateId: info.gateId,
				disabled: disabled
			}));
		}
	}, [dispatch]);

	const setWidgetDisabled = useCallback((el: HTMLElement) => {
		const info = decodeDomId(el.id);
		toggleWidget(true, info);
	}, [toggleWidget]);

	const setWidgetEnabled = useCallback((el: HTMLElement) => {
		const info = decodeDomId(el.id);
		toggleWidget(false, info);
	}, [toggleWidget]);

	const handleEditGroupWidget = useCallback((el: HTMLElement) => {
		const info = decodeDomId(el.id);
		if (info?.gateId) {
			dispatch(showCreateWidgetModalAction({
				recipeId: info?.recipeId,
				thingId: info?.blockId,
				widgetId: info?.gateId
			}));
		}
	}, [dispatch]);

	const handleEditWidgetBundle = useCallback((el: HTMLElement) => {
		const info = decodeDomId(el.id);
		if (info?.gateId) {
			dispatch(showCreateWidgetBundleModalAction({
				recipeId: info?.recipeId,
				thingId: info?.blockId,
				widgetId: info?.gateId
			}));
		}
	}, [dispatch]);

	const handleCreateCustomWidget = useCallback(() => {
		dispatch(showCreateWidgetModalAction(true));
	}, [dispatch]);

	const handleShowWidgetLauncher = useCallback(() => {
		dispatch(showWidgetLauncher({ visible: true, position: lastMouseClick }));
	}, [dispatch, lastMouseClick]);

	const handleCreateThirdPartyIntegration = useCallback(() => {
		dispatch(showCreateIntegrationModalAction(true));
	}, [dispatch]);

	// const handleCreateWidgetBundle = () => {
	// 	dispatch(showCreateWidgetBundleModalAction(true));
	// };

	const handleCopyWidgets = useCallback((el: HTMLElement) => {
		const info = decodeDomId(el.id);
		if (info?.gateId) {
			// dispatch(copySelectedWidgetsAction({
			// 	recipeId: info?.recipeId,
			// 	thingId: info?.blockId,
			// 	groupId: currentGroup?.groupId
			// }));
			copySelection({
				recipeId: info?.recipeId,
				thingId: info?.blockId,
				groupId: currentGroup?.groupId,
			});
		}
	}, [dispatch, currentGroup, copySelection]);

  const handleCutWidgets = useCallback((el: HTMLElement) => {
    const info = decodeDomId(el.id);
    if (info?.gateId) {
			cutSelection({
				recipeId: info?.recipeId,
				thingId: info?.blockId,
				groupId: currentGroup?.groupId,
			});
      // // First copy selection to clipboard/localStorage
      // dispatch(copySelectedWidgetsAction({
      //   recipeId: info?.recipeId,
      //   thingId: info?.blockId,
      //   groupId: currentGroup?.groupId
      // }));
      // // Then remove the widgets (mirror remove menu behavior)
      // const removeMultiple = Boolean(selectedWidgets.length);
      // const idsToRemove = removeMultiple ? selectedWidgets.map(w => w.widgetId) : [info.gateId];
      // dispatch(removeWidgetsAction({
      //   recipeId: info.recipeId,
      //   thingId: info.blockId,
      //   widgetsIds: idsToRemove,
      //   clearSelection: removeMultiple,
      // }));
      // if (removeMultiple) {
      //   props.plumbInstance?.clearDragSelection();
      // }
    }
  }, [dispatch, currentGroup, cutSelection]);

	const handleGroupWidgets = useCallback((el: HTMLElement) => {
		const info = decodeDomId(el.id);
		if (info) {
			dispatch(groupSelectedWidgetsAction({
				recipeId: info.recipeId,
				thingId: info.blockId,
				widgets: selectedWidgets,
				parentGroupId: currentGroup?.groupId,
			}));
		}
	}, [dispatch, selectedWidgets, currentGroup]);

	const handlePasteWidgets = useCallback(() => {
		if (currentBlock) {
			dispatch(pasteFromClipboardAction({
				recipeId: currentBlock.recipePoolId,
				thingId: currentBlock.recipeId
			}));
		}
	}, [dispatch, currentBlock]);

	const handleCreateSnippet = useCallback((el: HTMLElement) => {
		const info = decodeDomId(el.id);
		if (info?.gateId && currentBlock) {
			dispatch(createSnippetFromSelectionAction({
				recipeId: currentBlock.recipePoolId,
				thingId: currentBlock.recipeId,
				groupId: currentGroup?.groupId
			}));
		}
	}, [dispatch, currentBlock, currentGroup]);

	const pasteDisabled = useCallback(() => {
		const checkIfValid = async () => {
			const validClipboardData = await clipboardHasValidKemuStructure();
			setPasteEnabled(validClipboardData);
		};

		checkIfValid();
		return false;
	}, []);

	const checkIfCanHaveBreakpoint = useCallback((evt: PredicateParams<WidgetMenuProps>) => {
		if (!evt.props?.gateId) { return false; }
		if (evt.props.gateType === WidgetType.note) {
			return false;
		}

		return true;
	}, []);

	const checkIfBreakpointIsEnabled = useCallback((evt: PredicateParams<WidgetMenuProps>) => {
		const canHaveBreakpoint = checkIfCanHaveBreakpoint(evt);
		// NOTE: returning true here means that the item is hidden
		if (!canHaveBreakpoint) { return true; }

		if (!evt.props?.gateId) { return false; }
		const alreadySet = hasBreakpoint(evt.props.gateId);
		return alreadySet === true;
	}, [hasBreakpoint, checkIfCanHaveBreakpoint]);


	const checkIfBreakpointIsDisabled = useCallback((evt: PredicateParams<WidgetMenuProps>) => {
		// NOTE: returning true here means that the item is hidden
		const canHaveBreakpoint = checkIfCanHaveBreakpoint(evt);
		if (!canHaveBreakpoint) { return true; }

		return !checkIfBreakpointIsEnabled(evt);
	}, [checkIfBreakpointIsEnabled, checkIfCanHaveBreakpoint]);

	const handleSetBreakpoint = useCallback((el: HTMLElement) => {
		const info = decodeDomId(el.id);
		if (info?.gateId) {
			setBreakpoint(info.gateId);
		}
	}, [setBreakpoint]);

	const handleRemoveBreakpoint = useCallback((el: HTMLElement) => {
		const info = decodeDomId(el.id);
		if (info?.gateId) {
			removeBreakpoint(info.gateId);
		}
	}, [removeBreakpoint]);

	const handleUnlinkWidget = useCallback((el: HTMLElement) => {
		const info = decodeDomId(el.id);
		if (info?.gateId) {
			dispatch(unlinkCustomWidgetAction({
				recipeId: info.recipeId,
				thingId: info.blockId,
				widgetId: info.gateId
			}));
			message.success(t('Unlink.Success'));
		}
	}, [dispatch, t]);

	const handleResetWidgetFields = useCallback((el: HTMLElement) => {
		const info = decodeDomId(el.id);
		if (info?.gateId && info?.recipeId && info?.blockId) {
			try {
				resetWidgetGroupFields(info.recipeId, info.blockId, info.gateId);
				message.success(t('LogicMapper.ResetWidgetFields.Success'));
			} catch (error) {
				console.error('Failed to reset widget fields:', error);
				message.error(t('LogicMapper.ResetWidgetFields.Error'));
			}
		}
	}, [t]);

	/**
	 * Checks if a widget has changes or is new in execution mode.
	 * @param thingId - the id of the thing containing the widget.
	 * @param widgetId - the id of the widget.
	 * @returns true if the widget has state, position, or connection changes, or is new.
	 */
	const widgetHasChangesOrIsNew = useCallback((thingId: string, widgetId: string): boolean => {
		if (!executionMode || !editingSnapshot || !editingModeWidgetIds || !currentBlock?.recipePoolId) {
			return false;
		}

		const compositeId = `${thingId}:${widgetId}`;
		const editingModeWidgetIdsSet = new Set(editingModeWidgetIds);

		// Check if widget is new
		if (!editingModeWidgetIdsSet.has(compositeId)) {
			return true;
		}

		// Check if widget state or position has changed
		const currentRecipe = findRecipe(currentBlock.recipePoolId);
		if (!currentRecipe) { return false; }

		const currentWidget = currentRecipe.blocks?.[thingId]?.gates?.[widgetId];
		const snapshotWidget = editingSnapshot.blocks?.[thingId]?.gates?.[widgetId];

		if (!currentWidget || !snapshotWidget) { return false; }

		// Check state changes
		const currentStateStr = safeJSONStringify(currentWidget.state, true);
		const snapshotStateStr = safeJSONStringify(snapshotWidget.state, true);
		if (currentStateStr !== snapshotStateStr) {
			return true;
		}

		// Check canvas (position) changes
		const currentCanvasStr = JSON.stringify(currentWidget.canvas || {});
		const snapshotCanvasStr = JSON.stringify(snapshotWidget.canvas || {});
		if (currentCanvasStr !== snapshotCanvasStr) {
			return true;
		}

		// Check connection (children) changes
		const currentChildrenStr = JSON.stringify(currentWidget.children || []);
		const snapshotChildrenStr = JSON.stringify(snapshotWidget.children || []);
		if (currentChildrenStr !== snapshotChildrenStr) {
			return true;
		}

		return false;
	}, [executionMode, editingSnapshot, editingModeWidgetIds, currentBlock?.recipePoolId]);

	/**
	 * Handles preserving widget changes to the editing snapshot.
	 */
	const handlePreserveChanges = useCallback((el: HTMLElement) => {
		const info = decodeDomId(el.id);
		if (!info?.gateId || !currentBlock?.recipePoolId || !currentBlock?.recipeId) { return; }

		// Determine which widget IDs to preserve
		const widgetIdsToPreserve = selectedWidgets.length > 0
			? selectedWidgets.map(w => w.widgetId)
			: [info.gateId];

		dispatch(preserveWidgetChangesAction({
			recipePoolId: currentBlock.recipePoolId,
			thingId: currentBlock.recipeId,
			widgetIds: widgetIdsToPreserve,
		})).then(() => {
			message.success(t('LogicMapper.PreserveChanges.Success', 'Changes preserved'));
		}).catch((error) => {
			console.error('Failed to preserve changes:', error);
			message.error(t('LogicMapper.PreserveChanges.Error', 'Failed to preserve changes'));
		});
	}, [dispatch, currentBlock, selectedWidgets, t]);

	/**
	 * Determines if the Preserve Changes option should be hidden.
	 * Hidden when not in execution mode or when no widgets have changes.
	 */
	const shouldHidePreserveChanges = useCallback(({ props: menuProps }: PredicateParams<WidgetMenuProps>): boolean => {
		// Hide if not in execution mode
		if (!executionMode) { return true; }

		// Check if the right-clicked widget or any selected widget has changes
		const widgetsToCheck = selectedWidgets.length > 0
			? selectedWidgets.map(w => w.widgetId)
			: menuProps?.gateId ? [menuProps.gateId] : [];

		if (widgetsToCheck.length === 0) { return true; }

		const thingId = menuProps?.blockId || currentBlock?.recipeId;
		if (!thingId) { return true; }

		// Check if any widget has changes or is new
		const anyHasChanges = widgetsToCheck.some(widgetId => widgetHasChangesOrIsNew(thingId, widgetId));

		return !anyHasChanges;
	}, [executionMode, selectedWidgets, currentBlock?.recipeId, widgetHasChangesOrIsNew]);

	/**
	 * Hide the unlink widget option if:
	 * 1. The widget is not a group widget
	 * 2. The widget is already unlinked (no collectionInfo)
	 */
	const shouldHideUnlinkWidgetOption = useCallback(({ props }: PredicateParams<WidgetMenuProps>) => {
		// Hide if not a group widget
		if (props?.gateType !== WidgetType.widgetGroup) {
			return true;
		}

		// Hide if the widget is already unlinked (no collectionInfo)
		const widgetState = getState(props?.gateId);
		const isLinkedToOriginal = widgetState?.collectionInfo !== undefined;

		return !isLinkedToOriginal;
	}, [getState]);

	// const hidden = ({ /* triggerEvent, */ props, data }: PredicateParams<WidgetMenuProps>) => {
	// 	// Hide option for certain gates
	// 	const forbiddenWidgets = [WidgetType.input, WidgetType.play, WidgetType.display];
	// 	if (props && forbiddenWidgets.includes(props.gateType)) { return true; }


	// 	if (data.key === 'activate-return-original') {
	// 		return !!props?.returnOriginalEvent;
	// 	} else {
	// 		return !props?.returnOriginalEvent;
	// 	}
	// };

	return (
		<>
		<Menu id={MENU_GATE}>
			<Item
				data-kemu-meta="context-menu-copy"
				key='copy'
				disabled={!selectedWidgets.length}
				onClick={menuItemSelected}
				data={{ key: 'copy-widgets', handler: handleCopyWidgets }}
			>
				<ContextMenuItemContent icon={<CopyOutlined />} label={t('LogicMapper.Copy', 'Copy')} />
			</Item>

      <Item
        data-kemu-meta="context-menu-cut"
        key='cut'
        disabled={!selectedWidgets.length}
        onClick={menuItemSelected}
        data={{ key: 'cut-widgets', handler: handleCutWidgets }}
      >
        <ContextMenuItemContent icon={<ScissorOutlined />} label={t('LogicMapper.Cut', 'Cut')} />
      </Item>

			<Item
				data-kemu-meta="context-menu-create-snippet"
				key='createSnippet'
				disabled={!selectedWidgets.length}
				onClick={menuItemSelected}
				data={{ key: 'create-snippet', handler: handleCreateSnippet }}
			>
				<ContextMenuItemContent
					icon={<Icon component={SnippetsMenuIcon} />}
					label={t('LogicMapper.CreateSnippet', 'Create Snippet')}
				/>
			</Item>

			<Item
				data-kemu-meta="context-menu-group-widgets"
				key='group-widgets'
				disabled={showWhenMultipleWidgetsAreSelected}
				onClick={menuItemSelected}
				data={{ key: 'group-widgets', handler: handleGroupWidgets }}
			>
				<ContextMenuItemContent icon={<FolderOutlined />} label={t('LogicMapper.GroupWidgets', 'Group Widgets')} />
			</Item>

			<Item
				data-kemu-meta="context-menu-editWidget"
				key='editWidget'
				hidden={hideIfNotGroupWidget}
				onClick={menuItemSelected}
				data={{ key: 'edit-group-widget', handler: handleEditGroupWidget }}
			>
				<ContextMenuItemContent icon={<EditOutlined />} label={t('LogicMapper.EditGroup', 'Edit Group')} />
			</Item>

			<Item
				data-kemu-meta="context-menu-unlink-widget"
				key='unlinkWidget'
				hidden={shouldHideUnlinkWidgetOption}
				onClick={menuItemSelected}
				data={{ handler: handleUnlinkWidget }}
			>
				<ContextMenuItemContent icon={<DisconnectOutlined />} label={t('LogicMapper.UnlinkWidget', 'Unlink Widget')} />
			</Item>

			<Item
				data-kemu-meta="context-menu-disable"
				key='disable'
				hidden={hideIfDisabled}
				onClick={menuItemSelected}
				data={{ key: 'disable-widget', handler: setWidgetDisabled }}
			>
				<ContextMenuItemContent icon={<PauseCircleOutlined />} label={t('LogicMapper.Disable', 'Disable')} />
			</Item>

			<Item
				data-kemu-meta="context-menu-reset-widget-fields"
				key='resetWidgetFields'
				hidden={hideIfNotGroupWidget}
				onClick={menuItemSelected}
				data={{ handler: handleResetWidgetFields }}
			>
				<ContextMenuItemContent icon={<ReloadOutlined />} label={t('LogicMapper.ResetWidgetFields', 'Reset Widget Fields')} />
			</Item>

			<Item
				data-kemu-meta="context-menu-enable"
				key='enable'
				hidden={hideIfDisabled}
				onClick={menuItemSelected}
				data={{ key: 'enable-widget', handler: setWidgetEnabled }}
			>
				<ContextMenuItemContent icon={<PlayCircleOutlined />} label={t('LogicMapper.Enable', 'Enable')} />
			</Item>

			<Item
				data-kemu-meta="context-menu-duplicate"
				key='duplicate'
				hidden={hideIfWidgetGroup}
				onClick={menuItemSelected}
				data={{ handler: handleGateDuplication }}
			>
				<ContextMenuItemContent icon={<ForkOutlined />} label={t('LogicMapper.Duplicate', 'Duplicate')} />
			</Item>

			{/* <Item
				data-kemu-meta="context-menu-return-original-evt"
				key='return-original-evt'
				hidden={hidden}
				onClick={menuItemSelected}
				data={{ key: 'activate-return-original', handler: handleMakeOriginalEvent }}
			>
				{t('LogicMapper.ReturnOriginalEvent', 'Return Original Event')}
			</Item> */}

			{/* <Item
				data-kemu-meta="context-menu-return-result-evt"
				key='return-result-evt'
				hidden={hidden}
				onClick={menuItemSelected}
				data={{ key: 'cancel-return-original', handler: handleMakeOriginalEvent }}
			>
				{t('LogicMapper.ReturnProcessedEvent', 'Return Processed Event')}
			</Item> */}

			<Item
				data-kemu-meta="context-menu-remove-widget"
				key='remove-widget'
				onClick={menuItemSelected}
				data={{ handler: handleRemoveWidget }}>
				<ContextMenuItemContent
					icon={<DeleteOutlined />}
					label={selectedWidgets.length ? t('LogicMapper.RemoveSelected', 'Remove Selected') : t('LogicMapper.Remove', 'Remove')}
				/>
			</Item>

			<Item
				data-kemu-meta="context-menu-preserve-changes"
				key='preserve-changes'
				hidden={shouldHidePreserveChanges}
				onClick={menuItemSelected}
				data={{ handler: handlePreserveChanges }}
			>
				<ContextMenuItemContent icon={<SaveOutlined />} label={t('LogicMapper.PreserveChanges', 'Preserve Changes')} />
			</Item>

			{userSettings.showDebuggingPanel && (
				<Item
					disabled={true}
				 	key="breakpoint-separator"
					hidden={(evt) => !checkIfCanHaveBreakpoint(evt)}
					className={styles.DynamicSeparator}
				>
					<Separator />
				</Item>
			)}

			<Item
				data-kemu-meta="context-menu-breakpoint-add-breakpoint"
				key='add-breakpoint'
				onClick={menuItemSelected}
				hidden={!userSettings.showDebuggingPanel || checkIfBreakpointIsEnabled}
				data={{ handler: handleSetBreakpoint }}
			>
				<ContextMenuItemContent icon={<PlusCircleOutlined />} label={t('LogicMapper.AddBreakpoint')} />
			</Item>

			<Item
				data-kemu-meta="context-menu-breakpoint-remove-breakpoint"
				key='remove-breakpoint'
				onClick={menuItemSelected}
				hidden={!userSettings.showDebuggingPanel || checkIfBreakpointIsDisabled}
				data={{ handler: handleRemoveBreakpoint }}
			>
				<ContextMenuItemContent icon={<MinusCircleOutlined />} label={t('LogicMapper.RemoveBreakpoint')} />
			</Item>
		</Menu>

		{/* <Menu id={MENU_BLOCK}>
			<Item data-kemu-meta="context-menu-duplicate-widget" key='b1' onClick={menuItemSelected} data={handleBlockDuplication}>
				{t('LogicMapper.Canvas.Duplicate', 'Duplicate')}
			</Item>
		</Menu> */}

		<Menu id={MENU_LOGIC_MAKER}>
			<Item
				data-kemu-meta="context-menu-widget-launcher"
				key='lm_widget_launcher'
				onClick={menuItemSelected}
				data={{ handler: handleShowWidgetLauncher }}
			>
				<ContextMenuItemContent icon={<PlusCircleOutlined />} label={t('Canvas.AddWidgetOrService')} />
			</Item>

			<Item
				data-kemu-meta="context-menu-paste"
				key='paste'
				hidden={pasteDisabled}
				disabled={!pasteEnabled}
				onClick={menuItemSelected}
				data={{ key: 'paste-widgets', handler: handlePasteWidgets }}
			>
				<ContextMenuItemContent icon={<SnippetsOutlined />} label={t('LogicMapper.Paste', 'Paste')} />
			</Item>

			<Item
				data-kemu-meta="create-custom-widget-menu"
				key='lm_create_widget'
				onClick={menuItemSelected}
				data={{ handler: handleCreateCustomWidget }}
			>
				<ContextMenuItemContent icon={<AppstoreAddOutlined />} label={t('Canvas.CreateCustomWidget', 'Create custom widget')} />
			</Item>

			<Item
				data-kemu-meta="create-third-party-integration-menu"
				key='lm_create_integration'
				onClick={menuItemSelected}
				data={{ handler: handleCreateThirdPartyIntegration }}
			>
				<ContextMenuItemContent
					icon={<ApiOutlined />}
					label={t('LogicMapper.Canvas.CreateThirdPartyIntegration', 'Create Third Party Integration')}
				/>
			</Item>

			{/* <Item
				data-kemu-meta="create-widget-bundle-menu"
				key='lm_create_widget-bundle'
				onClick={menuItemSelected}
				hidden={!canCreateWidgetBundles}
				data={{ handler: handleCreateWidgetBundle }}
			>
				{t('Canvas.CreateWidgetBundle', 'Create widget bundle')}
			</Item> */}
		</Menu>
		</>
	);
};

export default ContextMenu;
